import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService, creditNoteService } from '../../../api/apiService';
import { sendCreditNoteEmails } from './creditNoteEmail';
import { STATUS_CREDIT_NOTE_PENDING } from '../../../helpers/bookingStatus';
import confirmIcon from '../../../images/confirm.svg';
import '../../../App.css';
import './ConfirmedListCreditNotes.css';

/**
 * The credit note queue: bookings whose talent has been confirmed and notified, and whose
 * Gutschrift is still outstanding (STATUS_CREDIT_NOTE_PENDING — set when the booking is
 * confirmed under Confirmed Orders with "Credit note" ticked).
 *
 * Creating the document here is step 2 of the client's process: it happens AFTER the job so
 * the tasks can still be corrected, several bookings of one talent can be combined onto one
 * PDF, and the PDF is emailed to the talent at that moment. From then on the document is
 * fixed — corrections go through a Storno and a new number.
 */
function ConfirmedListCreditNotes() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);
  const [createErrors, setCreateErrors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // "Generate the PDFs only after the jobs are completed" — on by default so a document
  // cannot be raised for a job that is still running and might still change.
  const [onlyFinished, setOnlyFinished] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingService.searchBookings({
        status: STATUS_CREDIT_NOTE_PENDING,
        limit: 500,
        offset: 0
      });
      const data = res.data;
      const list = data?.bookings || data || [];
      setBookings(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading confirmed bookings:', err);
      setError(err.message || 'Failed to load confirmed bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateCreditNote = async (booking) => {
    if (booking.credit_note_generated) return;
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings([booking.id]);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. Sending email…`);
        await load();
        const mail = await sendCreditNoteEmails(created);
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. `
          + (mail.failed.length ? 'Email FAILED — see below.' : 'Email sent.'));
        if (mail.failed.length) setCreateErrors(mail.failed);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit note']);
    } finally {
      setCreateLoading(false);
    }
  };

  /** A job counts as finished once its last day is over. */
  const isFinished = (b) => {
    const raw = b?.end_date || b?.start_date;
    if (!raw) return false;
    const end = typeof raw === 'string' ? new Date(raw) : raw;
    if (isNaN(end.getTime())) return false;
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime() < Date.now();
  };

  // Everything the buttons below act on. Hiding running jobs must also keep them out of
  // "select all" and "create for all" — otherwise the filter would be cosmetic only.
  const visible = useMemo(
    () => (onlyFinished ? bookings.filter(isFinished) : bookings),
    [bookings, onlyFinished]
  );
  const runningCount = bookings.length - bookings.filter(isFinished).length;

  const withoutNote = visible.filter((b) => !b.credit_note_generated);
  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllWithoutNote = () => {
    setSelectedIds(new Set(withoutNote.map((b) => b.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const isSelected = (id) => selectedIds.has(id);

  const handleCreateForSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setCreateMessage('Select at least one booking.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings(ids);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`${created.length} credit note(s) created (${created.map((c) => c.creditNoteNumber).join(', ')}). Sending emails…`);
        setSelectedIds(new Set());
        await load();
        const mail = await sendCreditNoteEmails(created);
        setCreateMessage(`${created.length} credit note(s) created. ${mail.sent.length} email(s) sent`
          + (mail.failed.length ? `, ${mail.failed.length} FAILED — see below.` : '.'));
        if (mail.failed.length) setCreateErrors(mail.failed);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit notes']);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateAllCreditNotes = async () => {
    if (withoutNote.length === 0) {
      setCreateMessage(onlyFinished && runningCount > 0
        ? `No finished jobs without a credit note (${runningCount} still running).`
        : 'No bookings without a credit note.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings(withoutNote.map(b => b.id));
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`${created.length} credit note(s) created (${created.map(c => c.creditNoteNumber).join(', ')}). Sending emails…`);
        await load();
        const mail = await sendCreditNoteEmails(created);
        setCreateMessage(`${created.length} credit note(s) created. ${mail.sent.length} email(s) sent`
          + (mail.failed.length ? `, ${mail.failed.length} FAILED — see below.` : '.'));
        if (mail.failed.length) setCreateErrors(mail.failed);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit notes']);
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? d : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const clientName = (b) => {
    if (!b?.client) return '—';
    const c = typeof b.client === 'string' ? (b.client && b.client.startsWith('{') ? JSON.parse(b.client) : {}) : b.client;
    return c?.name || c?.label || (typeof b.client === 'string' ? b.client : '—');
  };

  const talentDisplay = (b) => b.talent_name || b.talent_id || b.sql_talent_id || '—';
  const statusDisplay = (b) => (b.status ? String(b.status) : '—');
  // Agency name from the talent master data; short/placeholder values mean "no agency"
  const agencyDisplay = (b) => {
    const a = b.talent_agency != null ? String(b.talent_agency).trim() : '';
    return a.length > 1 ? a : '—';
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Pending credit notes</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Pending credit notes</h1>
        <p className="confirmed-cn-intro">
          Bookings whose talent is confirmed and whose Gutschrift is still outstanding. Create the
          document once the job is done: select several bookings of the same talent to put them on
          one PDF as multiple lines, or create per booking / for all. Creating it emails the PDF to
          the talent — after that the document is fixed and a correction needs a Storno and a new
          number, so check the tasks first.
        </p>
        <label className="confirmed-cn-intro" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={onlyFinished}
            onChange={(e) => { setOnlyFinished(e.target.checked); clearSelection(); }}
          />
          <span>
            Only jobs that have ended
            {runningCount > 0 && (
              <span className="cell-muted"> — {runningCount} still running{onlyFinished ? ' (hidden)' : ''}</span>
            )}
          </span>
        </label>
        <div className="confirmed-cn-actions">
          <button
            type="button"
            className="cn-btn cn-btn-primary"
            onClick={handleCreateForSelected}
            disabled={createLoading || selectedIds.size === 0}
            title="One credit note per talent; selected bookings from same talent become multiple lines on one PDF/XML"
          >
            {createLoading ? 'Creating…' : `Create credit note for selected (${selectedIds.size})`}
          </button>
          <button
            type="button"
            className="cn-btn cn-btn-secondary"
            onClick={selectAllWithoutNote}
            disabled={withoutNote.length === 0}
          >
            Select all without note
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={clearSelection} disabled={selectedIds.size === 0}>
            Clear selection
          </button>
          <button
            type="button"
            className="cn-btn cn-btn-primary"
            onClick={handleCreateAllCreditNotes}
            disabled={createLoading || withoutNote.length === 0}
          >
            {createLoading ? 'Creating…' : 'Create credit notes for all'}
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={() => navigate('/admin/credit-notes')}>
            View all credit notes
          </button>
        </div>
        {createMessage && (
          <div className="confirmed-cn-message success">
            {createMessage}
          </div>
        )}
        {createErrors.length > 0 && (
          <div className="confirmed-cn-message error">
            <strong>Could not create credit note(s) — missing data:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
              {createErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {error && (
          <div className="confirmed-cn-message error">
            {error}
          </div>
        )}
        <div className="content-wrapper content-wrapper-talent-list">
          <div className="list-tasks-header">
            <div style={{ flexBasis: '60px' }}>Select</div>
            <div style={{ flexBasis: '80px' }}>ID</div>
            <div style={{ flexBasis: '200px' }}>Title</div>
            <div style={{ flexBasis: '120px' }}>Start date</div>
            <div style={{ flexBasis: '120px' }}>End date</div>
            <div style={{ flexBasis: '150px' }}>Client</div>
            <div style={{ flexBasis: '120px' }}>Status</div>
            <div style={{ flexBasis: '150px' }}>Talent</div>
            <div style={{ flexBasis: '150px' }}>Agency</div>
            <div style={{ flexBasis: '80px' }}></div>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="confirmed-cn-empty">
            {bookings.length === 0
              ? 'No bookings are waiting for a credit note.'
              : `No finished jobs — all ${runningCount} booking(s) in the queue are still running.`}
          </div>
        ) : (
          visible.map((b) => (
            <div className="list-tasks-item" key={b.id} style={{ paddingLeft: '20px' }}>
              <div style={{ flexBasis: '60px' }}>
                {b.credit_note_generated ? (
                  '—'
                ) : (
                  <input
                    type="checkbox"
                    checked={isSelected(b.id)}
                    onChange={() => toggleSelection(b.id)}
                    aria-label={`Select booking ${b.id}`}
                  />
                )}
              </div>
              <div style={{ flexBasis: '80px' }}>{b.id}</div>
              <div style={{ flexBasis: '200px' }}>{b.title || '—'}</div>
              <div style={{ flexBasis: '120px' }}>{formatDate(b.start_date)}</div>
              <div style={{ flexBasis: '120px' }}>
                {formatDate(b.end_date)}
                {!isFinished(b) && <span className="cell-muted"> running</span>}
              </div>
              <div style={{ flexBasis: '150px' }}>{clientName(b)}</div>
              <div style={{ flexBasis: '120px' }}>{statusDisplay(b)}</div>
              <div style={{ flexBasis: '150px' }}>{talentDisplay(b)}</div>
              <div style={{ flexBasis: '150px' }}>{agencyDisplay(b)}</div>
              <div style={{ flexBasis: '80px' }}>
                {b.credit_note_generated ? (
                  <span className="cell-muted">Created</span>
                ) : (
                  <a
                    className={`edit-button ${createLoading ? 'disabled' : ''}`}
                    onClick={() => !createLoading && handleCreateCreditNote(b)}
                    title="Create credit note"
                    style={{
                      opacity: createLoading ? 0.5 : 1,
                      cursor: createLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <img src={confirmIcon} alt="Create credit note" className="edit-container" />
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ConfirmedListCreditNotes;
